export type NavigationIcon =
  | 'home' | 'booster' | 'collection' | 'anime' | 'fragment' | 'purge'
  | 'market' | 'trade' | 'quest' | 'calendar' | 'harvest'
  | 'social' | 'atlas' | 'referral' | 'message' | 'profile' | 'notification' | 'rules' | 'premium';

type NavigationBadge = 'home' | 'achievements' | 'login' | 'social' | 'messages' | 'trades';

export interface NavigationItem {
  to: string;
  labelKey: string;
  icon: NavigationIcon;
  badge?: NavigationBadge;
  rail?: false;
}

export interface NavigationSummary {
  claimableQuests: number;
  claimableLogin?: boolean;
  pendingFriendRequests: number;
  unreadConversations: number;
  pendingTrades: number;
  unreadNotifications: number;
}

export const HOME_NAV: NavigationItem = { to: '/', labelKey: 'nav.home', icon: 'home', badge: 'home' };

export const NAV_GROUPS: Array<{ id: string; labelKey: string; items: NavigationItem[] }> = [
  {
    id: 'play', labelKey: 'nav.groups.play',
    items: [
      { to: '/boosters', labelKey: 'nav.boosters', icon: 'booster' },
      { to: '/calendrier', labelKey: 'nav.calendar', icon: 'calendar', badge: 'login' },
      { to: '/quetes', labelKey: 'nav.quests', icon: 'quest', badge: 'achievements' },
      { to: '/moisson', labelKey: 'nav.harvest', icon: 'harvest' },
    ],
  },
  {
    id: 'collection', labelKey: 'nav.groups.collection',
    items: [
      { to: '/collection', labelKey: 'nav.collection', icon: 'collection' },
      { to: '/animes', labelKey: 'nav.animes', icon: 'anime' },
      { to: '/fragments', labelKey: 'nav.fragments', icon: 'fragment', rail: false },
      { to: '/purge', labelKey: 'nav.purge', icon: 'purge', rail: false },
    ],
  },
  {
    id: 'market', labelKey: 'nav.groups.market',
    items: [
      { to: '/marche', labelKey: 'nav.market', icon: 'market' },
      { to: '/echanges', labelKey: 'nav.trades', icon: 'trade', badge: 'trades' },
    ],
  },
  {
    id: 'community', labelKey: 'nav.groups.community',
    items: [
      { to: '/amis', labelKey: 'nav.friends', icon: 'social', badge: 'social' },
      { to: '/amis/atlas', labelKey: 'nav.atlas', icon: 'atlas', rail: false },
      { to: '/amis/parrainage', labelKey: 'nav.referral', icon: 'referral', rail: false },
      { to: '/messages', labelKey: 'nav.messages', icon: 'message', badge: 'messages' },
    ],
  },
  {
    id: 'account', labelKey: 'nav.groups.account',
    items: [
      { to: '/profil', labelKey: 'nav.profile', icon: 'profile' },
      { to: '/premium', labelKey: 'nav.premium', icon: 'premium' },
      { to: '/notifications', labelKey: 'nav.notifications', icon: 'notification', rail: false },
      { to: '/regles', labelKey: 'nav.rules', icon: 'rules', rail: false },
    ],
  },
];

const ALL_NAV = [HOME_NAV, ...NAV_GROUPS.flatMap((group) => group.items)];

export function activeNavigation(pathname: string): NavigationItem | undefined {
  if (pathname.startsWith('/personnages/')) return ALL_NAV.find((item) => item.to === '/collection');
  return ALL_NAV
    .filter((item) => pathname === item.to || (item.to !== '/' && pathname.startsWith(`${item.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0];
}

export function mobileSection(pathname: string): 'home' | 'collection' | 'booster' | 'market' | 'more' {
  const active = activeNavigation(pathname);
  if (active?.to === '/') return 'home';
  if (active?.to === '/boosters') return 'booster';
  if (active && NAV_GROUPS.find((group) => group.id === 'collection')?.items.includes(active)) return 'collection';
  if (active?.to === '/marche' || active?.to === '/echanges') return 'market';
  return 'more';
}

export function navigationBadgeCount(item: NavigationItem, achievements: number, summary?: NavigationSummary): number {
  if (item.badge === 'achievements') return achievements;
  if (item.badge === 'home') return achievements + (summary?.claimableQuests ?? 0) + Number(summary?.claimableLogin ?? false);
  if (!summary) return 0;
  if (item.badge === 'login') return Number(summary.claimableLogin ?? false);
  if (item.badge === 'social') return summary.pendingFriendRequests;
  if (item.badge === 'messages') return summary.unreadConversations;
  if (item.badge === 'trades') return summary.pendingTrades;
  return item.icon === 'notification' ? summary.unreadNotifications : 0;
}
